import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Skeleton, Stack } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import axios from 'axios';
import { useSelector } from 'react-redux';
import type { RootState } from '../../redux/root-reducer';
import { recentSocialPostsUrl } from '../../configurations/api/url';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { workProfileSectionTitleSx } from '../../constants/workProfileSectionTitle';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import type { IInfluencer } from '../../redux/buzzbridge/types';

/** Extract recent posts already embedded in the influencer's platform data. */
function extractStaticPosts(influencer: IInfluencer, platform: SupportedPlatform): ISocialPost[] {
    const data = (influencer as any)[platform];
    if (!data) return [];

    // Standard recent_posts array (TikTok, YouTube, future platforms)
    if (Array.isArray(data.recent_posts) && data.recent_posts.length > 0) {
        return data.recent_posts.map((p: any) => ({ ...p, platform }));
    }

    // Instagram falls back to metadata.reels_sample
    if (platform === 'instagram' && Array.isArray(data.metadata?.reels_sample)) {
        return data.metadata.reels_sample.map((r: any) => ({
            id: r.reel_id,
            caption: r.caption,
            media_type: 'VIDEO',
            timestamp: r.timestamp,
            like_count: r.metrics?.likes,
            comments_count: r.metrics?.comments,
            view_count: r.metrics?.views,
            platform,
        }));
    }

    return [];
}

export interface ISocialPost {
    id: string;
    caption?: string;
    media_type: 'VIDEO' | 'IMAGE' | 'CAROUSEL_ALBUM' | string;
    media_url?: string;
    permalink?: string;
    thumbnail_url?: string;
    timestamp: string;
    like_count?: number;
    comments_count?: number;
    view_count?: number;
    platform: string;
}

type SupportedPlatform = 'instagram' | 'tiktok' | 'youtube';
const SUPPORTED_PLATFORMS: SupportedPlatform[] = ['instagram', 'tiktok', 'youtube'];

function formatCount(n?: number): string {
    if (n == null) return '—';
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return String(n);
}

function PostCard({ post }: { post: ISocialPost }) {
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];
    const thumb = post.thumbnail_url || post.media_url;
    const isVideo = post.media_type === 'VIDEO';

    return (
        <Box
            component={post.permalink ? 'a' : 'div'}
            href={post.permalink}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '12px',
                overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.08)',
                backgroundColor: '#1a1a1a',
                textDecoration: 'none',
                cursor: post.permalink ? 'pointer' : 'default',
                transition: 'border-color 0.15s',
                '&:hover': post.permalink ? { borderColor: 'rgba(255,255,255,0.25)' } : {},
            }}>
            {/* Thumbnail */}
            <Box sx={{ position: 'relative', width: '100%', paddingTop: '100%', bgcolor: '#111', flexShrink: 0 }}>
                {thumb ? (
                    <Box
                        component="img"
                        src={thumb}
                        alt={post.caption ?? ''}
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                        }}
                    />
                ) : (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                        <PlayCircleOutlineIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.3)' }} />
                    </Box>
                )}
                {isVideo && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 6,
                            left: 6,
                            bgcolor: 'rgba(0,0,0,0.6)',
                            borderRadius: '4px',
                            px: 0.75,
                            py: 0.25,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.4,
                        }}>
                        <PlayCircleOutlineIcon sx={{ fontSize: 11, color: '#fff' }} />
                        <Typography sx={{ fontSize: 10, color: '#fff', fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                            VIDEO
                        </Typography>
                    </Box>
                )}
                {post.permalink && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 6,
                            right: 6,
                            bgcolor: 'rgba(0,0,0,0.6)',
                            borderRadius: '4px',
                            p: 0.4,
                            display: 'flex',
                        }}>
                        <OpenInNewIcon sx={{ fontSize: 12, color: '#fff' }} />
                    </Box>
                )}
            </Box>

            {/* Stats + caption */}
            <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75, flex: 1 }}>
                {post.caption && (
                    <Typography
                        sx={{
                            fontSize: 12,
                            color: t.primary_white,
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.4,
                        }}>
                        {post.caption}
                    </Typography>
                )}
                <Stack direction="row" spacing={1.5} sx={{ mt: 'auto' }}>
                    {post.like_count != null && (
                        <Stack direction="row" alignItems="center" spacing={0.4}>
                            <FavoriteIcon sx={{ fontSize: 11, color: '#E1306C' }} />
                            <Typography sx={{ fontSize: 11, color: t.form_label, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                {formatCount(post.like_count)}
                            </Typography>
                        </Stack>
                    )}
                    {post.comments_count != null && (
                        <Stack direction="row" alignItems="center" spacing={0.4}>
                            <ChatBubbleOutlineIcon sx={{ fontSize: 11, color: t.form_label }} />
                            <Typography sx={{ fontSize: 11, color: t.form_label, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                {formatCount(post.comments_count)}
                            </Typography>
                        </Stack>
                    )}
                    {post.view_count != null && (
                        <Stack direction="row" alignItems="center" spacing={0.4}>
                            <VisibilityIcon sx={{ fontSize: 11, color: t.form_label }} />
                            <Typography sx={{ fontSize: 11, color: t.form_label, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                {formatCount(post.view_count)}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            </Box>
        </Box>
    );
}

function PlatformSection({
    platform,
    authToken,
    compact,
    staticPosts,
}: {
    platform: SupportedPlatform;
    authToken?: string;
    compact?: boolean;
    staticPosts?: ISocialPost[];
}) {
    const { themeStyle } = useThemeContext();
    const [posts, setPosts] = useState<ISocialPost[]>(staticPosts ?? []);
    const [loading, setLoading] = useState(!staticPosts);

    const fetch = useCallback(async () => {
        if (!authToken) return;
        try {
            setLoading(true);
            const res = await axios.get(recentSocialPostsUrl(platform, 6), {
                headers: { Authorization: `Bearer ${authToken}` },
            });
            setPosts(res.data?.data?.posts ?? []);
        } catch {
            setPosts([]);
        } finally {
            setLoading(false);
        }
    }, [platform, authToken]);

    useEffect(() => {
        if (staticPosts) {
            setPosts(staticPosts);
            setLoading(false);
            return;
        }
        fetch();
    }, [staticPosts, fetch]);

    if (!loading && posts.length === 0) return null;

    const label = platform.charAt(0).toUpperCase() + platform.slice(1);

    return (
        <Box sx={{ width: '100%' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Typography sx={workProfileSectionTitleSx(themeStyle)}>{label}</Typography>
            </Stack>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: compact
                        ? { xs: 'repeat(3, 1fr)', sm: 'repeat(6, 1fr)' }
                        : { xs: 'repeat(2, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(2, 1fr)' },
                    gap: 1.5,
                }}>
                {loading
                    ? Array.from({ length: 6 }).map((_, i) => (
                          <Box key={i} sx={{ borderRadius: '12px', overflow: 'hidden' }}>
                              <Skeleton variant="rectangular" sx={{ paddingTop: '100%', width: '100%' }} />
                              <Skeleton variant="text" sx={{ mt: 0.5, mx: 1 }} width="70%" />
                              <Skeleton variant="text" sx={{ mb: 1, mx: 1 }} width="50%" />
                          </Box>
                      ))
                    : posts.slice(0, 6).map(post => <PostCard key={post.id} post={post} />)}
            </Box>
        </Box>
    );
}

interface RecentSocialPostsProps {
    influencer: IInfluencer | null | undefined;
    compact?: boolean;
    /** When true, reads recent_posts from the influencer object directly — no API calls. Use on public profiles. */
    publicView?: boolean;
}

export function RecentSocialPosts({ influencer, compact, publicView }: RecentSocialPostsProps) {
    const { themeStyle } = useThemeContext();
    const authToken = useSelector((state: RootState) => state.authentication.token as string);

    const linkedPlatforms = SUPPORTED_PLATFORMS.filter(p => (influencer as any)?.[p]?.linked === true);

    if (publicView) {
        // Use data already embedded in the influencer response — no API call, no auth required
        const platformsWithPosts = linkedPlatforms.filter(
            p => influencer && extractStaticPosts(influencer, p).length > 0,
        );
        if (platformsWithPosts.length === 0) return null;

        return (
            <Box
                sx={{
                    width: '100%',
                    backgroundColor: '#1a1a1a',
                    borderRadius: '14px',
                    border: '1px solid rgba(255,255,255,0.08)',
                    p: { xs: 2.5, sm: 3.5 },
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3.5,
                }}>
                <Typography sx={workProfileSectionTitleSx(themeStyle)}>Recent Posts</Typography>
                {platformsWithPosts.map(platform => (
                    <PlatformSection
                        key={platform}
                        platform={platform}
                        compact={compact}
                        staticPosts={extractStaticPosts(influencer!, platform)}
                    />
                ))}
            </Box>
        );
    }

    if (!authToken || linkedPlatforms.length === 0) return null;

    return (
        <Box
            sx={{
                width: '100%',
                backgroundColor: '#1a1a1a',
                borderRadius: '14px',
                border: '1px solid rgba(255,255,255,0.08)',
                p: { xs: 2.5, sm: 3.5 },
                display: 'flex',
                flexDirection: 'column',
                gap: 3.5,
            }}>
            <Typography sx={workProfileSectionTitleSx(themeStyle)}>Recent Posts</Typography>
            {linkedPlatforms.map(platform => (
                <PlatformSection key={platform} platform={platform} authToken={authToken} compact={compact} />
            ))}
        </Box>
    );
}
